import React from 'react';
import ChoicePicker from '../components/ChoicePicker';
import TextInput from '../components/TextInput';
import type { BankTypeCode, AccountTypeCode } from './bankAccountDetailsTypes';
import {
  ACCOUNT_NUM_MAX_LEN,
  ACCOUNT_TYPE_LABEL_JA,
  ACCOUNT_TYPE_OPTIONS,
  BANK_TYPE_LABEL_JA,
  BANK_TYPE_OPTIONS,
} from './bankAccountDetailsTypes';
import type { useBankAccountDetailsFlow } from './useBankAccountDetailsFlow';

type Flow = ReturnType<typeof useBankAccountDetailsFlow>;

const YES_NO = (t: Flow['t']): { value: 'yes' | 'no'; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.bankAccountDetails.yes') },
  { value: 'no', label: t('submitDocuments.bankAccountDetails.no') },
];

// Full-width + half-width Katakana, long vowel mark, middle dot, spaces
const KATAKANA_RE = /^[\u30A0-\u30FF\uFF65-\uFF9F\u3000\s・ー－-]+$/u;

export default function BankAccountDetailsChatComposer({ flow }: { flow: Flow }) {
  const { currentStep, t, currentValue, setField, advance } = flow;
  if (!currentStep) return null;

  const sendAria = t('submitDocuments.profileName.send');
  const requiredMsg = t('submitDocuments.workerRegistryChat.required');

  if (currentStep.kind === 'choice') {
    if (currentStep.stepId === 'consentGiven') {
      return (
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={(currentValue.trim() as any) || null}
            options={YES_NO(t)}
            onChange={(v) => {
              const choice = v === 'yes' ? 'yes' : 'no';
              setField('consentGiven', choice);
              void advance(choice);
            }}
          />
        </div>
      );
    }

    if (currentStep.stepId === 'bankType') {
      const opts = BANK_TYPE_OPTIONS.map((o) => ({ value: o.value, label: BANK_TYPE_LABEL_JA[o.value] }));
      return (
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={(currentValue.trim() as any) || null}
            options={opts}
            onChange={(v) => {
              setField('bankType', v as BankTypeCode);
              void advance(v);
            }}
          />
        </div>
      );
    }

    if (currentStep.stepId === 'accountType') {
      const opts = ACCOUNT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: ACCOUNT_TYPE_LABEL_JA[o.value] }));
      return (
        <div style={{ padding: '12px 16px' }}>
          <ChoicePicker
            value={(currentValue.trim() as any) || null}
            options={opts}
            onChange={(v) => {
              setField('accountType', v as AccountTypeCode);
              void advance(v);
            }}
          />
        </div>
      );
    }

    return null;
  }

  if (currentStep.stepId === 'accNum') {
    return (
      <TextInput
        fields={[
          {
            id: 'accNum',
            label: '',
            hideLabel: true,
            ariaLabel: t('submitDocuments.bankAccountDetails.steps.accNum'),
            required: true,
            maxLength: ACCOUNT_NUM_MAX_LEN,
            validate: (v) => (/^\d+$/.test(v) && v.length <= ACCOUNT_NUM_MAX_LEN ? null : t('submitDocuments.bankAccountDetails.invalidDigits')),
          },
        ]}
        values={{ accNum: currentValue }}
        onChange={(_id, v) => setField('accNum', v)}
        onSubmit={() => void advance()}
        submitAriaLabel={sendAria}
        requiredMessage={requiredMsg}
      />
    );
  }

  if (currentStep.stepId === 'nameKana') {
    return (
      <TextInput
        fields={[
          {
            id: 'nameKana',
            label: '',
            hideLabel: true,
            ariaLabel: t('submitDocuments.bankAccountDetails.steps.nameKana'),
            required: true,
            validate: (v) => (KATAKANA_RE.test(v) ? null : t('submitDocuments.bankAccountDetails.invalidKatakana')),
          },
        ]}
        values={{ nameKana: currentValue }}
        onChange={(_id, v) => setField('nameKana', v)}
        onSubmit={() => void advance()}
        submitAriaLabel={sendAria}
        requiredMessage={requiredMsg}
      />
    );
  }

  // bankName, bankTypeOther, branchName
  return (
    <TextInput
      fields={[
        {
          id: currentStep.fieldName,
          label: '',
          hideLabel: true,
          ariaLabel: t(`submitDocuments.bankAccountDetails.steps.${currentStep.stepId}`),
          required: true,
        },
      ]}
      values={{ [currentStep.fieldName]: currentValue }}
      onChange={(_id, v) => setField(currentStep.fieldName, v)}
      onSubmit={() => void advance()}
      submitAriaLabel={sendAria}
      requiredMessage={requiredMsg}
    />
  );
}

